import queryString from 'query-string';

// The booking list pages keep every filter in the URL so that going back from a
// booking detail page - browser back, a swipe gesture or the Go Back button -
// lands on the exact result set the admin came from.

const DEFAULT_FILTERS = {
  currentPage: 1,
  keyword: '',
  startDate: '',
  endDate: '',
  location: [],
  deliveryType: [],
  deliveryMethod: [],
};

// Multi-valued filters travel as one comma-joined value (location=1,2) rather
// than a repeated key, so the URL stays readable and the CSV route can parse
// them the same way.
const LIST_FILTERS = ['location', 'deliveryType', 'deliveryMethod'];

const BASE_PATHS = {
  completed: '/siteadmin/completed-bookings',
  failed: '/siteadmin/failed-bookings',
};

export function bookingListBasePath(requestType) {
  return BASE_PATHS[requestType] || '/siteadmin/bookings';
}

// query-string returns an array when a key is repeated in the URL.
function toText(value) {
  if (value === undefined || value === null) return '';
  return Array.isArray(value) ? String(value[0] || '') : String(value);
}

export function toList(value) {
  if (Array.isArray(value)) return value.filter(item => item !== '' && item != null);
  if (value === undefined || value === null || value === '') return [];
  return String(value).split(',').filter(item => item !== '');
}

export function parseBookingFilters(query = {}) {
  const page = parseInt(toText(query.page), 10);

  return {
    currentPage: page > 0 ? page : 1,
    keyword: toText(query.keyword),
    startDate: toText(query.startDate),
    endDate: toText(query.endDate),
    location: toList(query.location),
    deliveryType: toList(query.deliveryType),
    deliveryMethod: toList(query.deliveryMethod),
  };
}

function toParams(filters) {
  const { currentPage, ...rest } = { ...DEFAULT_FILTERS, ...filters };
  const params = {};

  Object.keys(rest).forEach(key => {
    if (LIST_FILTERS.includes(key)) {
      const values = toList(rest[key]);
      if (values.length) params[key] = values.join(',');
      return;
    }

    if (rest[key]) params[key] = rest[key];
  });

  return params;
}

// Returns '' when nothing is filtered, so an untouched list keeps a clean URL.
export function buildBookingListSearch(filters = {}) {
  const { currentPage } = { ...DEFAULT_FILTERS, ...filters };
  const params = toParams(filters);

  if (currentPage > 1) params.page = currentPage;

  const search = queryString.stringify(params);
  return search ? `?${search}` : '';
}

// The CSV route reads the same filters as the list, minus the page. Values are
// encoded here because a keyword containing & or # would otherwise cut the URL
// short and the export would run with the wrong filters.
export function buildExportUrl(exportType, filters = {}) {
  const params = { type: exportType, ...toParams(filters) };

  return `/export-admin-data?${queryString.stringify(params)}`;
}

function toIntList(value) {
  return toList(value)
    .map(item => parseInt(item, 10))
    .filter(item => Number.isInteger(item));
}

// Apollo's refetch merges into the variables of the previous call, so a key that
// is left out keeps its old value instead of being cleared. Every filter must
// therefore be sent on every call, explicitly nulled when it is not set.
export function toBookingQueryVariables(filters = {}, requestType) {
  const {
    currentPage,
    keyword,
    startDate,
    endDate,
    location,
    deliveryType,
    deliveryMethod,
  } = { ...DEFAULT_FILTERS, ...filters };

  const methods = toList(deliveryMethod);

  return {
    currentPage: currentPage || 1,
    keyword: keyword || '',
    requestType: requestType || null,
    // Sent as plain YYYY-MM-DD; the server widens them to the start and the end
    // of the day. Pre-converting here would apply the day boundary twice, once
    // in the browser's timezone and once in the server's.
    startDate: startDate || null,
    endDate: endDate || null,
    location: toIntList(location),
    deliveryType: toIntList(deliveryType),
    // Picking both methods is the same as picking neither, so the boolean is
    // only sent when the choice actually narrows anything.
    isDoorStepDelivery: methods.length === 1 ? methods[0] === 'delivery' : null,
  };
}
